import type { Aspect, Design, TimetableDay, Widget } from '@noorview/core';
import { TEMPLATES, addDays, clampRect, findSpot, minSize, newId, setAspect, templateWidgets } from '@noorview/core';

/* Studio rules shared by the web app and the iPhone app. */

/** A copy of `w` in the nearest free space of the same size (or nudged by one cell if the grid is full). */
export function duplicateWidget(d: Design, w: Widget): Widget {
  const spot = findSpot(d.widgets, w.w, w.h, d.cols, d.rows) ?? clampRect({ x: w.x + 1, y: w.y + 1, w: w.w, h: w.h }, d.cols, d.rows, minSize(w, d.cols, d.rows));
  return { ...structuredClone(w), id: newId(), ...spot, locked: false, name: w.name ? `${w.name} copy` : undefined };
}

/** Changing orientation: a template layout switches to that template's own version; a custom layout keeps its proportions. */
export function switchAspect(d: Design, a: Aspect): Design {
  const sig = (ws: Design['widgets']) => ws.map((w) => `${w.type}${w.x},${w.y},${w.w},${w.h}`).sort().join('|');
  const cur = sig(d.widgets);
  const t = d.widgets.length ? TEMPLATES.find((x) => sig(templateWidgets(x, d)) === cur) : undefined;
  const next = setAspect(d, a);
  if (!t) return next;
  // Keep each widget's settings, matched by type in order.
  const fresh = templateWidgets(t, next);
  const pool = [...d.widgets];
  return { ...next, widgets: fresh.map((f) => { const i = pool.findIndex((w) => w.type === f.type); const old = i >= 0 ? pool.splice(i, 1)[0]! : null; return old ? { ...old, x: f.x, y: f.y, w: f.w, h: f.h, props: f.type === 'prayerTimes' ? { ...old.props, layout: f.props.layout ?? 'auto' } : old.props } : f; }) };
}

/**
 * Example times for the design previews when the timetable has none to show (none uploaded yet, or Sehri and Tarawih
 * outside Ramadan), so each design can be seen as it will look rather than as an empty "add times" box.
 */
export function sampleTimetable(today: string): TimetableDay[] {
  return [-1, 0, 1, 2, 3, 4, 5, 6, 7].map((i) => {
    const date = addDays(today, i);
    const fri = new Date(`${date}T12:00:00Z`).getUTCDay() === 5;
    return { date, fajr: '05:40', fajrJamaah: '06:10', sunrise: '07:15', dhuhr: '12:20', dhuhrJamaah: '13:00', asr: '15:00', asrJamaah: '15:20', maghrib: '17:30', maghribJamaah: '17:35', isha: '18:55', ishaJamaah: '19:45', sehri: '05:30', tarawih: '20:00', ...(fri ? { jumuah1: '13:15', jumuah2: '14:00' } : {}) };
  });
}

export const ANNOUNCE_LENGTHS = [[30, '30 seconds'], [60, '1 minute'], [120, '2 minutes'], [300, '5 minutes'], [600, '10 minutes'], [900, '15 minutes'], [1800, '30 minutes'], [3600, '1 hour'], [0, 'Until I switch it off']] as const;
/** Widgets that stay as they are: the congregation always needs the prayer times, the time and the news. */
export const KEEP_WIDGETS = new Set(['prayerTimes', 'clock', 'ticker']);
/** A widget can become an announcement unless it's the screen's only prayer times, clock or news ticker. */
export const canSwap = (w: Widget, all: readonly Widget[]) => !KEEP_WIDGETS.has(w.type) || all.some((x) => x.id !== w.id && x.type === w.type && !x.hidden);
